/**
 * Income tax — what each person owes on the income they were assessed on, as a pure function.
 *
 * Given the two tax positions `incomeAssessment.ts` struck (assessable sources, deductions, franking
 * credits), this applies the tax system to each of them in the order the ATO does, which is the whole
 * substance of the module:
 *
 *   1. TAXABLE INCOME = salary + assessable investment income − deductions.
 *   2. GROSS TAX from the marginal brackets.
 *   3. FRANKING CREDITS come off first, as tax already paid by the company on the client's behalf.
 *   4. SAPTO (the seniors and pensioners offset) then LITO (the low-income offset), each capped at the
 *      tax still standing — the order matters, and neither is refundable: an offset cannot take the bill
 *      below zero or turn into a payment.
 *   5. THE MEDICARE LEVY is added last, and offsets do NOT reduce it — which is why a retiree with no
 *      income tax to pay can still owe the levy.
 *
 * Two things about the levy are worth knowing because they are easy to misread. It is assessed on the
 * PERSON but thresholded on the FAMILY: a couple is tested against the combined figure, and where they
 * are between the family thresholds each pays a share of the family levy in proportion to their own
 * income. And a senior or pensioner entitled to SAPTO is tested on higher thresholds than everyone else.
 *
 *   6. THE MEDICARE LEVY SURCHARGE is added after the levy for a person WITHOUT private hospital cover
 *      whose income for surcharge purposes (taxable income plus any net investment loss added back) puts
 *      the household past the base tier: 1%, 1.25% or 1.5% of that person's own income. Private cover
 *      avoids the surcharge and NOTHING ELSE — until 17 Sep 2026 the engine treated `person.phi` as
 *      exempting the levy itself, which understated a covered client's tax by 2% of their income.
 *
 * The offsets and brackets are passed in per call, not read from the constants: a projected year is taxed
 * against indexed rates, and `calculateFinancials` resolves those before calling in. That is also why the
 * Medicare calculation can live here at all — it used to be a closure inside `calculateFinancials` for
 * want of anywhere to put the `rates` it depends on.
 *
 * Extracted from `calculateFinancials`'s "--- 6. Tax Calculation ---" block, which still calls it —
 * through the injectable {@link IncomeTaxAssessor} seam, so the projection engine can hand in the
 * assessor owned by its incomeTaxPlugin.
 */

import { LITO, SAPTO, SAPTO_TAPER_RATE, MEDICARE_LEVY_RATE, MEDICARE_LEVY_SURCHARGE, PENSION_AGE, TAX_BRACKETS } from './constants/ratesAndThresholds.ts';
import { formatCurrency } from './support/currency.ts';
import type { PersonTaxPosition } from './incomeAssessment.ts';
import type { FactFindPerson } from './factFindTypes.ts';

/**
 * The rate at which the Medicare levy phases in between a threshold's lower and upper bound: 10c per
 * dollar of income above the lower one, until the full {@link MEDICARE_LEVY_RATE} applies to the whole
 * income instead.
 */
const MEDICARE_LEVY_SHADE_IN_RATE = 0.10;

/** Tax brackets with `previousLimit` filled in, so each bracket's width can be derived from it. */
export const TAX_BRACKETS_WITH_PREV = TAX_BRACKETS.map((b, i) => ({ ...b, previousLimit: i === 0 ? 0 : TAX_BRACKETS[i - 1]!.limit }));

/** The same for any bracket table — the year's own, chosen by financial year (see taxBracketsForFinancialYear). */
export function withPreviousLimits<T extends { limit: number }>(brackets: T[]): Array<T & { previousLimit: number }> {
    return brackets.map((b, i) => ({ ...b, previousLimit: i === 0 ? 0 : brackets[i - 1]!.limit }));
}

/** One line of the rendered tax working, as the UI's tax tooltip displays it. */
export interface TaxBreakdownLine {
    label: string;
    value: string;
}

/**
 * Tax on an income, stepped through the marginal brackets. Each bracket taxes only the slice of income
 * that falls inside it, which is what makes a "marginal rate" marginal.
 */
export const calculateTaxWithBrackets = (taxableIncome: number, brackets: any[]): number => {
    if (taxableIncome <= 0 || !brackets?.length) return 0;
    let tax = 0;
    let remainingIncome = taxableIncome;
    for (const bracket of brackets) {
        if (remainingIncome <= 0) break;
        const bracketWidth = bracket.limit === Infinity ? Infinity : bracket.limit - (bracket.previousLimit || 0);
        const taxableInBracket = Math.min(remainingIncome, bracketWidth);
        tax += taxableInBracket * bracket.rate;
        remainingIncome -= taxableInBracket;
    }
    return tax;
};

/** Tax at the current (unindexed) brackets. */
export const calculateTax = (taxableIncome: number, brackets: any[] = TAX_BRACKETS_WITH_PREV) => calculateTaxWithBrackets(taxableIncome, brackets);

/** The marginal rate an income lands in — the rate on its next dollar. */
export function getMarginalTaxRate(taxableIncome: number, brackets: any[]): number {
    const bracket = brackets.find((b) => taxableIncome <= b.limit) || brackets[brackets.length - 1];
    return bracket!.rate;
}

/** LITO entitlement from taxable income (non-refundable; does not reduce Medicare). */
export function calculateLitoEntitlement(taxableIncome: number, lito: typeof LITO = LITO): number {
    if (taxableIncome == null || taxableIncome <= 0) return 0;
    const ti = taxableIncome;
    if (ti <= lito.firstThreshold) return lito.maxOffset;
    if (ti <= lito.secondThreshold) {
        return Math.max(0, lito.maxOffset - (ti - lito.firstThreshold) * lito.firstTaperPerDollar);
    }
    if (ti < lito.cutOut) {
        const atSecondBandStart = lito.maxOffset - (lito.secondThreshold - lito.firstThreshold) * lito.firstTaperPerDollar;
        return Math.max(0, atSecondBandStart - (ti - lito.secondThreshold) * lito.secondTaperPerDollar);
    }
    return 0;
}

/** SAPTO entitlement from rebate income (here: taxable income proxy). */
export function calculateSaptoEntitlement(
    rebateIncome: number,
    band: { maxOffset: number; shadeIn: number; cutOut: number } | null,
    taperRate: number = SAPTO_TAPER_RATE,
): number {
    if (!band) return 0;
    const ri = rebateIncome == null ? 0 : rebateIncome;
    if (ri <= band.shadeIn) return band.maxOffset;
    if (ri >= band.cutOut) return 0;
    return Math.max(0, band.maxOffset - taperRate * (ri - band.shadeIn));
}

/** The offset tables at their current, unindexed values. */
export function buildBaseTaxOffsets() {
    return {
        lito: { ...LITO },
        sapto: {
            single: { ...SAPTO.single },
            coupleMember: { ...SAPTO.coupleMember },
            illnessSeparatedMember: { ...SAPTO.illnessSeparatedMember },
        },
    };
}

/**
 * The offset tables with every dollar threshold scaled (and rounded) by `scale`, for a projected year's
 * indexed rates. The taper RATES are proportions, not amounts, so they are carried through untouched.
 */
export function scaleTaxOffsets(scale: (n: number) => number) {
    const s = (n: number) => Math.round(scale(n));
    return {
        lito: {
            maxOffset: s(LITO.maxOffset),
            firstThreshold: s(LITO.firstThreshold),
            secondThreshold: s(LITO.secondThreshold),
            cutOut: s(LITO.cutOut),
            firstTaperPerDollar: LITO.firstTaperPerDollar,
            secondTaperPerDollar: LITO.secondTaperPerDollar,
        },
        sapto: {
            single: { maxOffset: s(SAPTO.single.maxOffset), shadeIn: s(SAPTO.single.shadeIn), cutOut: s(SAPTO.single.cutOut) },
            coupleMember: {
                maxOffset: s(SAPTO.coupleMember.maxOffset),
                shadeIn: s(SAPTO.coupleMember.shadeIn),
                cutOut: s(SAPTO.coupleMember.cutOut),
            },
            illnessSeparatedMember: {
                maxOffset: s(SAPTO.illnessSeparatedMember.maxOffset),
                shadeIn: s(SAPTO.illnessSeparatedMember.shadeIn),
                cutOut: s(SAPTO.illnessSeparatedMember.cutOut),
            },
        },
    };
}

/** The levy's threshold sets: the general pair, and the higher pair for a person entitled to SAPTO. */
export interface MedicareLevyThresholds {
    single: { lower: number; upper: number };
    couple: { lower: number; upper: number };
    singleSenior?: { lower: number; upper: number };
    coupleSenior?: { lower: number; upper: number };
}

/**
 * The Medicare levy for ONE person, thresholded on the FAMILY where they are partnered.
 *
 * Nil below the person's own lower threshold, the full rate on the whole income above the upper one, and
 * shaded in at {@link MEDICARE_LEVY_SHADE_IN_RATE} between them. For a couple the family income decides
 * which of those three applies, and in the shade-in band the family levy is apportioned by the person's
 * share of that income. `isSenior` — entitled to SAPTO — selects the higher senior thresholds. Private
 * health cover does NOT reduce the levy (it avoids the surcharge, {@link calculateMedicareLevySurcharge}).
 */
export function calculateMedicareLevy(
    taxableIncome: number,
    relationshipStatus: string,
    isSenior: boolean,
    familyTaxableIncome: number,
    medicareLevyThresholds: MedicareLevyThresholds,
): number {
    if (taxableIncome <= 0) return 0;

    const singleThresholds = (isSenior && medicareLevyThresholds.singleSenior) || medicareLevyThresholds.single;
    if (relationshipStatus === 'single') {
        if (taxableIncome <= singleThresholds.lower) return 0;
        if (taxableIncome > singleThresholds.upper) return taxableIncome * MEDICARE_LEVY_RATE;
        return (taxableIncome - singleThresholds.lower) * MEDICARE_LEVY_SHADE_IN_RATE;
    }

    const coupleThresholds = (isSenior && medicareLevyThresholds.coupleSenior) || medicareLevyThresholds.couple;
    if (familyTaxableIncome <= coupleThresholds.lower) return 0;

    if (taxableIncome <= singleThresholds.lower) return 0;

    if (familyTaxableIncome > coupleThresholds.upper) {
        return taxableIncome * MEDICARE_LEVY_RATE;
    }

    const familyLevy = (familyTaxableIncome - coupleThresholds.lower) * MEDICARE_LEVY_SHADE_IN_RATE;
    return familyTaxableIncome > 0 ? familyLevy * (taxableIncome / familyTaxableIncome) : 0;
}

/** The surcharge's tiers — `singleUpTo` / `familyUpTo` are the top of each tier; the last is open. */
export interface MedicareLevySurchargeRates {
    tiers: Array<{ singleUpTo: number; familyUpTo: number; rate: number }>;
    perExtraChild: number;
}

/**
 * The Medicare levy surcharge for ONE person: nil with private hospital cover, nil where their own
 * income is at or under the levy's low-income threshold, otherwise the tier rate set by the SINGLE or
 * FAMILY income for surcharge purposes, applied to the person's OWN income for surcharge purposes.
 * Income for surcharge purposes is taxable income with any net investment loss added back (the
 * reportable fringe benefits and super contributions the ATO also adds are not modelled — D.3.30).
 */
export function calculateMedicareLevySurcharge(
    mlsIncome: number,
    relationshipStatus: string,
    hasPhi: boolean,
    familyMlsIncome: number,
    lowIncomeThreshold: number,
    rates: MedicareLevySurchargeRates = MEDICARE_LEVY_SURCHARGE,
): number {
    if (hasPhi || mlsIncome <= 0 || mlsIncome <= lowIncomeThreshold) return 0;
    const tested = relationshipStatus === 'single' ? mlsIncome : familyMlsIncome;
    const tier = rates.tiers.find((t) => tested <= (relationshipStatus === 'single' ? t.singleUpTo : t.familyUpTo)) ?? rates.tiers[rates.tiers.length - 1]!;
    return mlsIncome * tier.rate;
}

/** Taxable income with a net investment loss added back — what the surcharge is tested on. */
export function incomeForSurchargePurposesOf(position: PersonTaxPosition): number {
    // Salary reaches the position as a named source among the investment ones (see PersonTaxPosition),
    // so it is set aside by name: a net investment loss is deductions in excess of INVESTMENT income.
    const investmentIncome = position.investmentSources
        .filter((s) => !SALARY_LIKE.test(s.name))
        .reduce((sum, s) => sum + s.amount, 0);
    const deductions = position.deductionSources.reduce((sum, d) => sum + d.amount, 0);
    const netInvestmentLoss = Math.max(0, deductions - investmentIncome);
    return totalTaxableIncomeOf(position) + netInvestmentLoss;
}

/** How a salary source is named in a tax position — the tool's own descriptions and a client's. */
const SALARY_LIKE = /salary|wage|employment/i;

/** The total assessable income in a tax position: salary plus every source, less every deduction. */
export function totalTaxableIncomeOf(position: PersonTaxPosition): number {
    return (
        position.salary +
        position.investmentSources.reduce((sum, source) => sum + source.amount, 0) -
        position.deductionSources.reduce((sum, deduction) => sum + deduction.amount, 0)
    );
}

/** What one person owes. */
export interface PersonIncomeTax {
    /** Tax payable after offsets, plus the Medicare levy. Negative where franking credits exceed the tax. */
    netTax: number;
    /** The taxable income the tax was struck on. */
    totalTaxable: number;
    /** That person's section of the rendered working. Empty for an absent partner. */
    breakdown: TaxBreakdownLine[];
}

/** The tax positions to be taxed, and the rates and status to tax them under. */
export interface IncomeTaxInput {
    /** The rate tables for the year — read for `taxBrackets`, `medicareLevyThresholds` and `taxOffsets`. */
    rates: any;
    /** The two positions `incomeAssessment.ts` struck, with any loan-interest deductions already appended. */
    clientTaxable: PersonTaxPosition;
    partnerTaxable: PersonTaxPosition;
    isPartnered: boolean;
    /** Illness-separated couples are each entitled to the higher SAPTO band. */
    illnessSeparated: boolean;
    /** `'couple'` or `'single'` — which Medicare levy thresholds apply. */
    relationshipStatus: string;
    /** Read for `firstName` (breakdown labels) and `phi` (private hospital cover — avoids the surcharge). */
    client: FactFindPerson;
    partner: FactFindPerson;
    /** SAPTO requires pension age. */
    clientAge: number | null;
    partnerAge: number | null;
}

/** What the household owes. */
export interface IncomeTaxAssessment {
    client: PersonIncomeTax;
    /** All zeros for a single client — nothing is assessed against an absent partner. */
    partner: PersonIncomeTax;
    /** The household's tax — the two `netTax` figures summed. This is what the cashflow is charged. */
    totalTax: number;
    /** Both people's working, concatenated, as the UI renders it. */
    taxBreakdown: TaxBreakdownLine[];
    /** Combined taxable income, which decides the couple's Medicare levy thresholds. */
    familyTaxableIncome: number;
}

/**
 * Assesses the household's income tax. Injected into `calculateFinancials` so a caller can own the
 * rules — see {@link calculateIncomeTax}, the default, and the projection engine's incomeTaxPlugin.
 */
export type IncomeTaxAssessor = (input: IncomeTaxInput) => IncomeTaxAssessment;

/** No tax, no working — an absent partner. */
const NO_TAX: PersonIncomeTax = { netTax: 0, totalTaxable: 0, breakdown: [] };

/**
 * Taxes one person: their taxable income, the tax on it, the offsets against that tax in statutory
 * order, and the levy on top — rendering the working line by line as it goes.
 */
function taxOnePerson(
    personName: string,
    taxable: PersonTaxPosition,
    phi: boolean | undefined,
    relationshipStatus: string,
    familyTaxableIncome: number,
    familyMlsIncome: number,
    personAge: number | null,
    saptoBand: any,
    rates: any,
    lito: typeof LITO,
): PersonIncomeTax {
    const totalInvestmentIncome = taxable.investmentSources.reduce((sum, s) => sum + s.amount, 0);
    const totalDeductions = taxable.deductionSources.reduce((sum, d) => sum + d.amount, 0);
    const totalTaxable = taxable.salary + totalInvestmentIncome - totalDeductions;
    const grossTax = calculateTaxWithBrackets(totalTaxable, rates.taxBrackets);
    // Pension age is the tool's proxy for SAPTO entitlement (as it is for the offset itself below).
    const isSenior = personAge != null && personAge >= PENSION_AGE;
    const medicare = calculateMedicareLevy(totalTaxable, relationshipStatus, isSenior, familyTaxableIncome, rates.medicareLevyThresholds);
    const levyLowerThreshold = ((isSenior && rates.medicareLevyThresholds.singleSenior) || rates.medicareLevyThresholds.single).lower as number;
    const surcharge = calculateMedicareLevySurcharge(
        incomeForSurchargePurposesOf(taxable),
        relationshipStatus,
        phi === true,
        familyMlsIncome,
        levyLowerThreshold,
        rates.medicareLevySurcharge ?? MEDICARE_LEVY_SURCHARGE,
    );
    // Franking credits are tax the company already paid on this income, so they come off before any
    // offset is considered. This can go negative — a refund.
    const incomeTaxBeforeOffsets = grossTax - taxable.frankingCredits;
    const saptoEligible = personAge != null && personAge >= PENSION_AGE && saptoBand;
    // Rebate income is properly taxable income plus certain add-backs; taxable income stands in for it.
    const rebateIncomeProxy = totalTaxable;
    const saptoRaw = saptoEligible ? calculateSaptoEntitlement(rebateIncomeProxy, saptoBand, SAPTO_TAPER_RATE) : 0;
    // Non-refundable: each offset is capped at the tax still standing, and SAPTO is applied first.
    const saptoUsed = Math.min(saptoRaw, Math.max(0, incomeTaxBeforeOffsets));
    const afterSapto = incomeTaxBeforeOffsets - saptoUsed;
    const litoRaw = calculateLitoEntitlement(totalTaxable, lito);
    const litoUsed = Math.min(litoRaw, Math.max(0, afterSapto));
    // Offsets particular income earns (the 10% on an untaxed-source defined benefit pension): also
    // non-refundable, taken last against whatever still stands.
    const incomeOffsetsRaw = taxable.taxOffsets ?? 0;
    const incomeOffsetsUsed = Math.min(incomeOffsetsRaw, Math.max(0, afterSapto - litoUsed));
    // The levy and the surcharge are added AFTER the offsets, which is why they survive them.
    const netTax = afterSapto - litoUsed - incomeOffsetsUsed + medicare + surcharge;

    const breakdown: TaxBreakdownLine[] = [
        { label: `--- ${personName}'s Tax ---`, value: '' },
        { label: 'Salary Income', value: formatCurrency(taxable.salary) },
        { label: 'Assessable Investment Income', value: formatCurrency(totalInvestmentIncome) },
    ];
    taxable.investmentSources.forEach((source) => {
        breakdown.push({ label: `  - ${source.name}`, value: formatCurrency(source.amount) });
    });
    breakdown.push({ label: 'Less: Deductions', value: formatCurrency(-totalDeductions) });
    taxable.deductionSources.forEach((deduction) => {
        breakdown.push({ label: `  - ${deduction.name}`, value: formatCurrency(-deduction.amount) });
    });
    breakdown.push(
        { label: 'Total Taxable Income', value: formatCurrency(totalTaxable) },
        { label: `Marginal Tax Rate`, value: `${getMarginalTaxRate(totalTaxable, rates.taxBrackets) * 100}%` },
        { label: 'Gross Tax Payable', value: formatCurrency(grossTax) },
        { label: 'Medicare Levy', value: formatCurrency(medicare) },
    );
    if (surcharge > 0) {
        breakdown.push({ label: 'Medicare Levy Surcharge (no private hospital cover)', value: formatCurrency(surcharge) });
    }
    breakdown.push({ label: 'Less: Franking Credits', value: formatCurrency(-taxable.frankingCredits) });
    if (saptoUsed > 0) {
        breakdown.push({ label: 'Less: SAPTO (Seniors & Pensioners)', value: formatCurrency(-saptoUsed) });
    }
    if (litoUsed > 0) {
        breakdown.push({ label: 'Less: Low Income Tax Offset (LITO)', value: formatCurrency(-litoUsed) });
    }
    if (incomeOffsetsUsed > 0) {
        breakdown.push({ label: 'Less: Defined benefit pension offset (10%, untaxed source)', value: formatCurrency(-incomeOffsetsUsed) });
    }
    breakdown.push({ label: 'Net Tax Payable', value: formatCurrency(netTax) });
    return { breakdown, netTax, totalTaxable };
}

/**
 * Taxes both people.
 *
 * The family total has to be struck BEFORE either person is taxed, because it decides their Medicare
 * levy thresholds — which is why this is one assessment of a household rather than two of individuals.
 */
export const calculateIncomeTax: IncomeTaxAssessor = (input: IncomeTaxInput): IncomeTaxAssessment => {
    const { rates, clientTaxable, partnerTaxable, isPartnered, illnessSeparated, relationshipStatus, client, partner, clientAge, partnerAge } = input;

    const taxOffsets = rates.taxOffsets || buildBaseTaxOffsets();
    const saptoBandForPerson = !isPartnered ? taxOffsets.sapto.single : illnessSeparated ? taxOffsets.sapto.illnessSeparatedMember : taxOffsets.sapto.coupleMember;

    const clientTotalTaxable = totalTaxableIncomeOf(clientTaxable);
    const partnerTotalTaxable = isPartnered ? totalTaxableIncomeOf(partnerTaxable) : 0;
    const familyTaxableIncome = clientTotalTaxable + partnerTotalTaxable;
    const familyMlsIncome = incomeForSurchargePurposesOf(clientTaxable) + (isPartnered ? incomeForSurchargePurposesOf(partnerTaxable) : 0);

    const clientTax = taxOnePerson(
        client.firstName || 'Client',
        clientTaxable,
        client.phi,
        relationshipStatus,
        familyTaxableIncome,
        familyMlsIncome,
        clientAge,
        saptoBandForPerson,
        rates,
        taxOffsets.lito,
    );
    const partnerTax = isPartnered
        ? taxOnePerson(
              partner.firstName || 'Partner',
              partnerTaxable,
              partner.phi,
              relationshipStatus,
              familyTaxableIncome,
              familyMlsIncome,
              partnerAge,
              saptoBandForPerson,
              rates,
              taxOffsets.lito,
          )
        : NO_TAX;

    return {
        client: clientTax,
        partner: partnerTax,
        totalTax: clientTax.netTax + partnerTax.netTax,
        taxBreakdown: [...clientTax.breakdown, ...partnerTax.breakdown],
        familyTaxableIncome,
    };
};
